@layer fonts {
	@font-face {
		font-family: "Name Sans";
		src: url("./style/fonts/atnamesansvariable-regular-basic.woff2") format("woff2");
		font-weight: 1 1000;
		font-style: normal;
		ascent-override: 97.5%;
	}

	@font-face {
		font-family: "Name Sans";
		src: url("./style/fonts/atnamesansvariable-regular-extended.woff2") format("woff2");
		font-weight: 1 1000;
		font-style: normal;
		ascent-override: 97.5%;
		unicode-range:
			U+00a0-00FF, U+0100-017E, U+0192-0237, U+02BB-02DD, U+0300-0328, U+0394-03C0, U+1E9E-1EF9;
	}

	@font-face {
		font-family: "Name Mono";
		src: url("./style/fonts/atnamemonovariable-regular-basic.woff2") format("woff2");
		font-weight: 100 900;
		font-style: normal;
	}

	@font-face {
		font-family: "Name Mono";
		src: url("./style/fonts/atnamemonovariable-regular-extended.woff2") format("woff2");
		font-weight: 100 900;
		font-style: normal;
		unicode-range:
			U+00a0-00FF, U+0100-017E, U+0192-0237, U+02BB-02DD, U+0300-0328, U+0394-03C0, U+1E9E-1EF9;
	}
}

@layer reset {
	*,
	*::before,
	*::after {
		box-sizing: border-box;
	}

	* {
		margin: 0;
		padding: 0;
	}

	:root {
		interpolate-size: allow-keywords;
	}

	body {
		line-height: 1.5;
	}

	:where(img, picture, video, canvas, svg) {
		display: block;
		max-inline-size: 100%;
	}

	:where(input, button, textarea, select) {
		font: inherit;
		letter-spacing: inherit;
		word-spacing: inherit;
		color: currentColor;
		border: none;
		background-color: transparent;
	}

	:where(h1, h2, h3, h4, h5, h6) {
		line-height: 1.25;
		text-wrap: balance;
		letter-spacing: -0.025ch;
	}

	:where(p, h1, h2, h3, h4, h5, h6) {
		overflow-wrap: break-word;
		font-weight: normal;
	}

	:where(ol, ul, menu) {
		list-style: none;
	}

	p:not([class]) {
		margin-block: 1em;
		margin-trim: block;

		&:first-child {
			margin-block-start: 0;
		}

		&:last-child {
			margin-block-end: 0;
		}
	}
}

@layer theme {
	/* set variables for our design tokens */
	:root {
		/* fonts */
		--font-body: "Name Sans", system-ui, sans-serif;
		--font-mono: "Name Mono", ui-monospace, monospace;

		/* sizes */
		--size-border: 1px;
		--size-border-lg: 2px;
		--size-outline: 3px;
		--size-1: 2px;
		--size-2: 4px;
		--size-3: 8px;
		--size-4: 12px;
		--size-5: 16px;
		--size-6: 20px;
		--size-7: 24px;
		--size-8: 28px;
		--size-9: 32px;
		--size-10: 48px;
		--size-11: 64px;

		/* corner radii */
		--radius-xs: 2px;
		--radius-sm: 4px;
		--radius-md: 6px;
		--radius-lg: 8px;
		--radius-xl: 12px;
		--radius-max: 33554400px;

		/* text sizes */
		--text-legal: calc(2rem / 3);
		--text-caption: 0.75rem;
		--text-body: 0.875rem;
		--text-subtitle-2: 1rem;
		--text-subtitle-1: 1.25rem;
		--text-title: 1.75rem;

		/* colors */
		--blue-100: #ebebff;
		--blue-200: #d6d6ff;
		--blue-300: #9d9dfb;
		--blue-400: #6464fe;
		--blue-500: #4747ff;
		--blue-600: #1111ff;
		--blue-700: #0000df;
		--blue-800: #010198;
		--blue-900: #00005e;
		--green-100: #eefcf2;
		--green-200: #ddf8e5;
		--green-300: #bbf2cb;
		--green-400: #99ebb1;
		--green-500: #55dd7e;
		--green-600: #33d764;
		--green-700: #25bb52;
		--green-800: #1b883c;
		--green-900: #115525;
		--red-100: #fbeef1;
		--red-200: #f7dee3;
		--red-300: #efbdc8;
		--red-400: #e48b9f;
		--red-500: #d85a75;
		--red-600: #d0395a;
		--red-700: #b62b49;
		--red-800: #95233c;
		--red-900: #631728;
		--orange-100: #fff6eb;
		--orange-200: #ffedd6;
		--orange-300: #ffdaad;
		--orange-400: #ffb65c;
		--orange-500: #ff8d01;
		--orange-600: #e07b00;
		--orange-700: #b86500;
		--orange-800: #7a4300;
		--orange-900: #522d00;
		--gray-100: #fcfcff;
		--gray-200: #f7f7fd;
		--gray-300: #efeff9;
		--gray-400: #e1e1f0;
		--gray-500: #a3a3c7;
		--gray-600: #66668b;
		--gray-700: #414162;
		--gray-800: #2c2c46;
		--gray-900: #1b1b33;

		--color-background: light-dark(var(--gray-300), var(--gray-900));
		--color-text: light-dark(var(--gray-900), var(--gray-400));
		--color-text-subtle: light-dark(var(--gray-700), var(--gray-500));
		--color-text-silent: var(--gray-600);
		--color-error-text: var(--red-500);
		--color-primary-text: light-dark(var(--blue-600), var(--blue-300));
		--color-primary: var(--blue-400);
		--color-primary-hover: light-dark(var(--blue-500), var(--blue-300));
		--color-primary-foreground: light-dark(var(--blue-500), var(--blue-300));
		--color-primary-background: light-dark(
			rgb(from var(--blue-600) r g b / 10%),
			rgb(from var(--blue-400) r g b / 20%)
		);
		--color-success-foreground: light-dark(var(--green-800), var(--green-600));
		--color-success-background: light-dark(
			rgb(from var(--green-600) r g b / 15%),
			rgb(from var(--green-500) r g b / 15%)
		);
		--color-error-foreground: light-dark(var(--red-600), var(--red-500));
		--color-error-background: rgb(from light-dark(var(--red-600), var(--red-500)) r g b / 15%);
		--color-warning-foreground: light-dark(var(--orange-600), var(--orange-400));
		--color-warning-background: rgb(
			from light-dark(var(--orange-600), var(--orange-400)) r g b / 15%
		);
		--color-surface: light-dark(var(--gray-100), var(--gray-800));
		--color-surface-elevated: light-dark(var(--gray-200), var(--gray-700));
		--color-surface-hover: light-dark(var(--gray-200), var(--gray-700));
		--color-border: light-dark(
			rgb(from var(--gray-500) r g b / 20%),
			rgb(from var(--gray-100) r g b / 15%)
		);
		--color-border-hover: light-dark(
			rgb(from var(--gray-500) r g b / 40%),
			rgb(from var(--gray-100) r g b / 30%)
		);
		--color-border-focus: light-dark(var(--blue-500), var(--blue-400));
		--color-border-active: light-dark(
			rgb(from var(--gray-500) r g b / 30%),
			rgb(from var(--gray-100) r g b / 20%)
		);
		--color-shadow: light-dark(
			hsl(from var(--blue-700) h s l / 5%),
			hsl(from var(--gray-900) h s calc(l / 2) / 25%)
		);
		--color-outline: light-dark(
			rgb(from var(--color-primary) r g b / 25%),
			rgb(from var(--gray-100) r g b / 20%)
		);

		/* shadows */
		--shadow-src-x: 0;
		--shadow-src-y: -1;
		--shadow-blur: max(abs(var(--shadow-src-x)), abs(var(--shadow-src-y)));
		--shadow-sm: calc(var(--shadow-src-x) * 0.5px) calc(var(--shadow-src-y) * -1px)
			calc(var(--shadow-blur) * 1px) var(--color-shadow);
		--shadow-md:
			calc(var(--shadow-src-x) * 1px) calc(var(--shadow-src-y) * -2px)
				calc(var(--shadow-blur) * 2px) calc(var(--shadow-blur) * -1px) var(--color-shadow),
			calc(var(--shadow-src-x) * 2px) calc(var(--shadow-src-y) * -4px)
				calc(var(--shadow-blur) * 4px) calc(var(--shadow-blur) * -2px) var(--color-shadow),
			calc(var(--shadow-src-x) * 3px) calc(var(--shadow-src-y) * -6px)
				calc(var(--shadow-blur) * 6px) calc(var(--shadow-blur) * -4px) var(--color-shadow);
		--shadow-lg:
			calc(var(--shadow-src-x) * 1px) calc(var(--shadow-src-y) * -2px)
				calc(var(--shadow-blur) * 2px) calc(var(--shadow-blur) * -1px) var(--color-shadow),
			calc(var(--shadow-src-x) * 2px) calc(var(--shadow-src-y) * -4px)
				calc(var(--shadow-blur) * 4px) calc(var(--shadow-blur) * -2px) var(--color-shadow),
			calc(var(--shadow-src-x) * 4px) calc(var(--shadow-src-y) * -8px)
				calc(var(--shadow-blur) * 8px) calc(var(--shadow-blur) * -6px) var(--color-shadow),
			calc(var(--shadow-src-x) * 8px) calc(var(--shadow-src-y) * -16px)
				calc(var(--shadow-blur) * 16px) calc(var(--shadow-blur) * -12px) var(--color-shadow),
			calc(var(--shadow-src-x) * 16px) calc(var(--shadow-src-y) * -32px)
				calc(var(--shadow-blur) * 32px) calc(var(--shadow-blur) * -24px) var(--color-shadow);
	}

	@supports (corner-shape: squircle) {
		*,
		*::before,
		*::after {
			corner-shape: squircle;
		}

		:root {
			--radius-xs: 4px;
			--radius-sm: 8px;
			--radius-md: 12px;
			--radius-lg: 16px;
			--radius-xl: 24px;
		}
	}

	/*
		set the color scheme, depending on the system theme and explicit user preference.
		if we need to query the color theme from within CSS, we can use style queries:

		@container style(--theme: dark) { ... }
	*/

	:root {
		/* support both light and dark themes, defaulting to light */
		color-scheme: light dark;

		/* set the --theme variable to light or dark, depending on the system theme */
		--theme: light;
		@media (prefers-color-scheme: dark) {
			--theme: dark;
		}

		/* override the color scheme and --theme variable if the user explicitly prefers light */
		&[data-theme="light"] {
			color-scheme: light;
			--theme: light;
		}

		/* override the color scheme and --theme variable if the user explicitly prefers dark */
		&[data-theme="dark"] {
			color-scheme: dark;
			--theme: dark;
		}
	}
}

@layer base {
	html {
		overflow: hidden;
	}

	/* enable child elements to reach "full height" by setting height: 100% */
	html,
	body {
		height: 100%;
	}

	/* fix finger overscroll */
	:root,
	body {
		--color-link: light-dark(var(--blue-500), var(--blue-300));
		overscroll-behavior: none;
	}

	*::-webkit-scrollbar {
		width: 14px;
	}

	*::-webkit-scrollbar-track {
		background: var(--color-background);
	}

	*::-webkit-scrollbar-thumb {
		background-color: light-dark(var(--gray-500), var(--gray-700));
		border-radius: 10px;
		border: 4px solid var(--color-background);
	}

	*::-webkit-scrollbar-thumb:hover {
		background-color: light-dark(var(--gray-600), var(--gray-600));
	}

	/** disable text selection and cursors on native */
	html[data-native] {
		&,
		& dialog {
			-webkit-user-select: none;
			user-select: none;
			overflow-x: clip;
		}

		:where(a) {
			cursor: default;
		}
	}

	/** use cursor pointers only in the web app */
	html:not([data-native])
		:where(
			button,
			summary,
			select,
			a[href],
			[role="button"],
			[popovertarget],
			input:is([type="checkbox"], [type="radio"], [type="file"]),
			label:has(input:is([type="checkbox"], [type="radio"], [type="file"]))
		):not(:where(:disabled, [aria-disabled="true"])) {
		cursor: pointer;
	}

	/* make text selection match our theme */
	::selection {
		background-color: var(--color-primary);
		color: var(--blue-100);
	}

	html {
		font-family: var(--font-body);
		font-feature-settings: "ss08", "tnum";
		color: var(--color-text);
		background-color: var(--color-background);

		&.transparent {
			background-color: transparent;
		}
	}

	body {
		min-height: 100dvh;
		font-size: var(--text-body);
	}

	#app {
		display: contents;
	}

	:focus-visible {
		outline-offset: 0;
		outline: var(--size-outline) solid var(--color-outline);
		border-radius: var(--radius-sm);
	}

	a {
		color: var(--color-link);
		text-decoration: none;

		&:hover {
			text-decoration: underline;
			text-decoration-thickness: 0.1em;
			text-underline-offset: 0.15em;
		}
	}

	code {
		font-family: var(--font-mono);
	}

	input[type="checkbox"] {
		--checkbox-color-background: light-dark(var(--gray-200), var(--gray-700));
		--checkbox-color-background-hover: light-dark(var(--gray-300), var(--gray-600));
		--checkbox-color-border: var(--color-border);
		display: block;
		appearance: none;
		width: 1rem;
		height: 1rem;
		flex-shrink: 0;
		border-radius: var(--radius-sm);
		background: border-box var(--checkbox-color-background) var(--checkbox-image) center no-repeat;
		border: var(--size-border) solid var(--checkbox-color-border);
		transition:
			background-color 0.1s ease,
			box-shadow 0.1s ease,
			border-color 0.1s ease;

		&:hover {
			--checkbox-color-border: var(--color-border-hover);
			--checkbox-color-background: light-dark(var(--gray-300), var(--gray-600));
		}

		&:active {
			box-shadow: inset 0 1px 1px #00000044;
		}

		&:focus-visible {
			--checkbox-color-border: var(--color-border-hover);
			outline-offset: 0;
		}

		&:checked {
			--checkbox-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'><polyline points='2.5 9.5 5.5 12.5 13.5 3.5' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke' /></svg>");
		}

		&:indeterminate {
			--checkbox-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'><line x1='4' y1='8' x2='12' y2='8' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke' /></svg>");
		}

		&:checked,
		&:indeterminate {
			--checkbox-color-background: var(--color-primary);
			box-shadow: var(--shadow-sm);

			&:hover {
				--checkbox-color-background: var(--color-primary-hover);
			}
		}
	}

	h1 {
		font-size: var(--text-title);
	}
}
